import DashboardService from "@/src/api/DashboardService.js";
import CRMService from "@/src/api/CRMService.js";

import {
  dateFilterTypes,
  datePresetOptions,
  excludedFilterFields,
  excludedFilterTypes,
  ATTRIBUTE_TYPES,
  standardObjectStatusFields,
  currentUserId,
  getFilterTypeFromDatatype,
  buildFilterFlags,
  booleanOptions,
} from "@/src/constants/crmConstants.js";
import { fetchCountryOptions } from "@/src/utils/dynamicFormUtils.js";
import { getEntityTypeFromSource } from "@/src/constants/crmConstants.js";
import store from "@/src/store/index.js";
import { getSourceOptionsFromAttribute } from "@/src/utils/schemaUtils.js";
import { standardObjects } from "@/src/constants/objectConstants.js";
import { toSnakeCase } from "@/src/utils/analyticUtils";
import { entityTypes } from "../constants/entityTypeConstants";
import { closeDateOptions } from "@/src/constants/filterConstants.js";

export const toFilterType = (datatypeOrType) => {
  const dynamicMap = {
    string: "text",
    text: "text",
    personal_name: "text",
    email: "text",
    phone_number: "text",
    url: "text",
    domain: "text",
    number: "number",
    currency: "currency",
    date: "date",
    datetime: "date",
    boolean: "select",
    select: "select",
    multi_select: "select",
    status: "select",
    user: "user",
    file: "text",
    image: "text",
    relation_record: "relation_record",
  };

  const key = String(datatypeOrType || "").toLowerCase();
  if (key in dynamicMap) return dynamicMap[key];
  return getFilterTypeFromDatatype(datatypeOrType);
};

export const isMultiSelect = (field) => {
  const { type, datatype, is_multi_value } = field || {};
  if (is_multi_value) return true;
  const dt = String(datatype || "").toLowerCase();
  if (dt === ATTRIBUTE_TYPES.BOOLEAN) return false;
  if (dt === ATTRIBUTE_TYPES.DATE || dt === ATTRIBUTE_TYPES.DATETIME)
    return false;

  const t = String(type || datatype || "").toLowerCase();
  if (t === ATTRIBUTE_TYPES.BOOLEAN) return false;
  if (t === ATTRIBUTE_TYPES.DATE || t === ATTRIBUTE_TYPES.DATETIME)
    return false;
  if (
    t === ATTRIBUTE_TYPES.MULTI_SELECT ||
    t === "tags" ||
    t === ATTRIBUTE_TYPES.USER ||
    t === ATTRIBUTE_TYPES.SELECT ||
    t === ATTRIBUTE_TYPES.RECORD ||
    t === "status"
  )
    return true;
  return false;
};

export const isDateFilterId = (id) => {
  return dateFilterTypes.includes(id);
};

export const getDatePresets = (entityType = null) => {
  if (entityType === entityTypes.TICKETS || entityType === entityTypes.TASKS) {
    return datePresetOptions.slice();
  }

  return closeDateOptions.slice();
};

export const normalizeOption = (opt) => ({
  id: typeof opt === "string" ? opt : (opt?.id ?? opt),
  name: typeof opt === "string" ? opt : (opt?.name ?? opt),
  selected: false,
  ...(typeof opt === "object" && opt?.isDeleted ? { isDeleted: true } : {}),
});

const mapStandardSlugToViewSlug = (slug) => {
  if (!slug) return slug;
  const lower = String(slug).toLowerCase();
  const found = (standardObjects || []).find(
    (o) => String(o?.slug || "").toLowerCase() === lower
  );
  return found ? found.viewSlug : slug;
};

export const filterOutExcluded = (list = [], options = {}) => {
  const { allowIdFilter = false } = options;
  const excludedFields = new Set(excludedFilterFields);
  // Sequential-id objects (e.g. invoices with INV-N) filter by id meaningfully,
  // so honour the caller's opt-in and keep the id filter option visible.
  if (allowIdFilter) excludedFields.delete("id");
  const excludedTypes = new Set(excludedFilterTypes);

  return list.filter((item) => {
    const fieldSlug = String(item?.id || item?.slug);
    if (excludedFields.has(fieldSlug)) {
      return false;
    }

    // Only check datatype (original property type) for type-based exclusion
    const itemDatatype = String(item?.datatype || "").toLowerCase();

    if (excludedTypes.has(itemDatatype)) {
      return false;
    }

    return true;
  });
};

export const loadFilterOptions = async (filter, ctx = {}) => {
  const attributeId = String(
    filter.attribute_id || filter.id || ""
  ).toLowerCase();
  const filterId = String(filter.id || filter.slug || "").toLowerCase();
  const filterName = String(filter.name || "").toLowerCase();
  const fieldType = String(filter.type || filter.datatype || "").toLowerCase();
  const searchQuery = (ctx.searchQuery || "").toLowerCase().trim();
  const entityType = ctx.entityType || null;

  if (
    filter.datatype === ATTRIBUTE_TYPES.BOOLEAN ||
    fieldType === ATTRIBUTE_TYPES.BOOLEAN
  ) {
    return booleanOptions.map((opt) => ({
      id: opt.value,
      name: opt.label,
      selected: false,
    }));
  }

  if (fieldType === ATTRIBUTE_TYPES.DATE || isDateFilterId(filterId)) {
    return getDatePresets(entityType).map(normalizeOption);
  }

  // Handle record type filters - search across related objects
  if (fieldType === ATTRIBUTE_TYPES.RECORD ) {
    const relatedObjects = filter.related_objects || [];
    if (relatedObjects.length === 0) {
      return [];
    }

    try {
      const objectIds = relatedObjects.map((ro) => ro.object_id);
      const response = await CRMService.searchMultiObjects({
        object_ids: objectIds,
        query: searchQuery || "",
        limit: 50,
      });

      if (response?.success && response?.results) {
        const options = [];
        response.results.forEach((objResult) => {
          (objResult.records || []).forEach((record) => {
            options.push({
              id: record.id,
              name: record?.attributes?.display_name || record.display_name || record.id,
              object_id: objResult.object_id,
              object_type: objResult.object_type,
              object_name: objResult.object_name,
              selected: false,
            });
          });
        });
        return options;
      }
    } catch (e) {
      console.error("Error fetching record filter options:", e);
    }
    return [];
  }

  if (
    (fieldType === ATTRIBUTE_TYPES.SELECT ||
      fieldType === ATTRIBUTE_TYPES.MULTI_SELECT) &&
    (attributeId === "country" ||
      filterId === "country" ||
      filterName === "country" ||
      filterName === "countries")
  ) {
    if (filter.options && filter.options.length > 0) {
      let countries = filter.options.map(normalizeOption);
      if (searchQuery) {
        countries = countries.filter((c) =>
          (c.name || "").toLowerCase().includes(searchQuery)
        );
      }
      return countries;
    }

    try {
      const opts = await fetchCountryOptions();
      let countries = (opts || []).map((o) => ({
        id: o.value,
        name: o.label,
        selected: false,
      }));

      if (searchQuery) {
        countries = countries.filter((c) =>
          c.name.toLowerCase().includes(searchQuery)
        );
      }

      return countries;
    } catch (e) {
      console.error("Error fetching country options for filter:", e);
      return [];
    }
  }

  // Lead source field - fetch from schema only
  if (
    attributeId.includes("source") ||
    filterId.includes("source") ||
    filterName.includes("source")
  ) {
    const schema = store.getters["schema/getSchemaBySlug"]("leads");
    const sourceAttr = schema?.attributes?.find(
      (a) => a.slug === "source" || a.id === "source"
    );

    let sources = getSourceOptionsFromAttribute(sourceAttr)
      .map((s) => ({ id: s, name: s, selected: false }));

    if (searchQuery) {
      sources = sources.filter((s) =>
        (s.name || "").toLowerCase().includes(searchQuery)
      );
    }
    return sources;
  }

  if (filter.options && filter.options.length > 0) {
    let options = filter.options.map(normalizeOption);
    if (searchQuery) {
      options = options.filter((opt) =>
        (opt.name || "").toLowerCase().includes(searchQuery)
      );
    }
    return options;
  }

  if (
    attributeId.includes("owner") ||
    filterId.includes("owner") ||
    filterId.includes("user") ||
    fieldType === ATTRIBUTE_TYPES.USER ||
    filter.datatype === ATTRIBUTE_TYPES.USER
  ) {
    let users = store.getters["users/usersWithDeleted"];
    let activeUsers = store.getters["users/users"];

    const fetchPromises = [];
    if (!users || users.length === 0) {
      fetchPromises.push(store.dispatch("users/fetchUsersWithDeleted"));
    }
    if (!activeUsers || activeUsers.length === 0) {
      fetchPromises.push(store.dispatch("users/fetchUsers"));
    }
    if (fetchPromises.length > 0) {
      await Promise.all(fetchPromises);
      users = store.getters["users/usersWithDeleted"];
      activeUsers = store.getters["users/users"];
    }

    // Fall back to activeUsers if usersWithDeleted is unavailable
    if (!users || !Array.isArray(users) || users.length === 0) {
      users = activeUsers;
    }

    if (users && Array.isArray(users)) {
      const onlyMeOption = {
        id: currentUserId,
        name: "Only Me",
        isDeleted: false,
        selected: false,
      };

      const activeUsersMap = new Map((activeUsers || []).map((u) => [String(u.id), u]));

      const userOptions = users.map((user) => {
        const storeUser = activeUsersMap.get(String(user.id));
        return {
          id: user.id,
          name: user.name || user.email,
          avatar_color: storeUser?.avatar_color ?? user.avatar_color,
          profile_pic: storeUser?.profile_pic ?? user.profile_pic,
          isDeleted: user.isDeleted || false,
          selected: false,
        };
      });

      return [onlyMeOption, ...userOptions];
    }

    return [];
  }

  if (
    attributeId.includes("team") ||
    filterId.includes("team") ||
    filterName.includes("team")
  ) {
    const response = await DashboardService.fetchTeams();
    if (response?.success) {
      return (response.teams || []).map((team) => ({
        id: team.id,
        name: team.name,
        selected: false,
      }));
    }
    return [];
  }

  const isTagsFilter = (
    (attributeId === "tags" || attributeId.startsWith("tags_")) ||
    (filterId === "tags" || filterId.startsWith("tags_")) ||
    (filterName === "tags" || filterName.startsWith("tags ")) ||
    fieldType === "tags"
  );

  if (isTagsFilter) {
    const entityType = ctx.isDynamicCRM
      ? mapStandardSlugToViewSlug(ctx.slug)
      : getEntityTypeFromSource(ctx.source);
    let response;
    if (ctx.isDynamicCRM && ctx.slug) {
      response = await CRMService.getTagsForEntity(ctx.slug);
    } else {
      switch (entityType) {
        case "contact":
          response = await DashboardService.getTagsForContactFilters();
          break;
        case "lead":
          response = await DashboardService.getTagsForLeadFilters();
          break;
        case "company":
          response = await DashboardService.getTagsForCompanyFilters();
          break;
        case "deal":
          response = await DashboardService.getTagsForDealFilters();
          break;
        default:
          response = await DashboardService.getTagsForContactFilters();
      }
    }
    if (response?.success) {
      return (response.tags || []).map((tag) => {
        if (typeof tag === "string") {
          return { id: tag, name: tag, selected: false };
        }

        const id = tag.id || tag.value;
        const name = tag.name || tag.label;
        return { id, name, selected: false };
      });
    }
    return [];
  }

  if (
    standardObjectStatusFields.includes(attributeId) ||
    standardObjectStatusFields.includes(filterId)
  ) {
    const entityType = ctx.isDynamicCRM
      ? mapStandardSlugToViewSlug(ctx.slug)
      : getEntityTypeFromSource(ctx.source);

    const isStandardObject = standardObjects.some(
      (obj) => obj.viewSlug === entityType || obj.slug === ctx.slug
    );

    if (!isStandardObject) {
      return [];
    }

    try {
      const response = await DashboardService.fetchObjectPropertyStatus(
        entityType,
        "status"
      );

      if (response?.success && response?.property_details?.options) {
        return response.property_details.options.map((opt) => ({
          id: toSnakeCase(opt),
          name: opt,
          selected: false,
        }));
      }
    } catch (e) {
      console.error("Error fetching status options:", e);
    }
    return [];
  }

  if (
    ["current_company", "deal_company", "company"].includes(attributeId) ||
    ["current_company", "deal_company", "company"].includes(filterId) ||
    filterId === "company"
  ) {
    const entityType = ctx.isDynamicCRM
      ? mapStandardSlugToViewSlug(ctx.slug)
      : getEntityTypeFromSource(ctx.source);

    const isStandardObject = standardObjects.some(
      (obj) => obj.viewSlug === entityType || obj.slug === ctx.slug
    );

    if (!isStandardObject) {
      return [];
    }

    try {
      const response = await CRMService.searchDynamicObjects({
        object_type: 'companies',
        search_query: searchQuery || '',
        page: 1,
        page_size: 100,
      });

      if (response?.success) {
        return (response.results || []).map((company) => {
          const attrs = company?.attributes || {};
          return {
            id: parseInt(company?.id, 10),
            name: attrs?.name?.value || attrs?.name || null,
            selected: false,
          };
        });
      }
    } catch (e) {
      console.error("Error fetching companies:", e);
    }
    return [];
  }

  if (
    attributeId.includes("industry") ||
    filterId.includes("industry") ||
    filterName.includes("industry")
  ) {
    const entityType = ctx.isDynamicCRM
      ? mapStandardSlugToViewSlug(ctx.slug)
      : getEntityTypeFromSource(ctx.source);

    if (entityType !== entityTypes.COMPANY) {
      return [];
    }

    try {
      const OutreachService = (await import("@/src/api/OutreachService.js"))
        .default;
      const response = await OutreachService.campaignFilters(
        "industry",
        searchQuery || ""
      );
      if (response?.success && response?.options) {
        return response.options.map((industry) => ({
          id: industry,
          name: industry,
          selected: false,
        }));
      }
    } catch (e) {
      console.error("Error fetching industries:", e);
    }
    return [];
  }

  if (["text", "number", "currency"].includes(fieldType)) {
    return [];
  }

  return [];
};

export const convertToApiSlug = (slug) => {
  if (!slug) return slug;

  const normalized = slug.toLowerCase();

  const standardObj = standardObjects.find(
    (obj) => obj.viewSlug === normalized
  );
  if (standardObj) {
    return standardObj.slug;
  }

  const standardObjPlural = standardObjects.find(
    (obj) => obj.slug === normalized
  );
  if (standardObjPlural) {
    return standardObjPlural.slug;
  }

  return slug.replace(/\s+/g, "_");
};

export const applyFilterFlags = (filter) => {
  const fieldType = String(filter.type || filter.datatype || "").toLowerCase();
  const flags = buildFilterFlags(fieldType, filter);
  Object.assign(filter, flags);
  return filter;
};

export const extractAttrString = (val) => {
  if (typeof val === "string") return val;
  if (val && typeof val === "object" && "value" in val) return String(val.value ?? "");
  return "";
};

export const extractEmailFromAttr = (emailAttr) => {
  if (!emailAttr) return null;
  if (typeof emailAttr === "string") return emailAttr;
  if (emailAttr && typeof emailAttr === "object" && "value" in emailAttr) {
    return String(emailAttr.value ?? "");
  }
  if (Array.isArray(emailAttr) && emailAttr.length > 0) {
    const primaryEmail = emailAttr.find((e) => e.is_primary) || emailAttr[0];
    return primaryEmail?.email || null;
  }
  return null;
};

export const transformCRMSearchResult = (item, prefix = "") => {
  const attrs = item?.attributes || {};
  return {
    ...item,
    id: prefix ? prefix + item.id : item.id,
    first_name: extractAttrString(attrs.first_name) || undefined,
    last_name: extractAttrString(attrs.last_name) || undefined,
    name: extractAttrString(attrs.name) || undefined,
    email: extractEmailFromAttr(attrs.email) || null,
    website: extractAttrString(attrs.website) || null,
    industry: extractAttrString(attrs.industry) || null,
    logo_url: extractAttrString(attrs.logo_url) || null,
    location: extractAttrString(attrs.location) || null,
  };
};
